import React from "react";
import ReactDOM from "react-dom/client";
import { keys as localDbKeys, del as localDbDelete } from "idb-keyval";
import { ErrorBoundary } from "react-error-boundary";

import App from "./App/App";
import ErrorPage from "components/ErrorPage";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { Amplify, Auth } from "aws-amplify";
import { CookieStorage } from '@aws-amplify/auth'
import awsExports from "./aws-exports";
import Cookie from "js-cookie";
import { getCurrentSession } from "helpers/apiHelpers";

import "antd/dist/antd.css";
import "styles/generic.scss";
import "styles/table.scss";
import OnlineContextProvider from "helpers/contexts/OnlineContext/OnlineContext";
import EntityDataProvider from "EntityDataContext/EntityDataContext";
import { logOut } from "helpers/generalHelpers";

const getSharedHostname = () => {
  const hostname = window.location.hostname

  if (hostname === 'localhost') {
    return hostname
  }

  return hostname.substring(hostname.indexOf('.'))
}

Amplify.configure({
  ...awsExports,
  API: {
    graphql_headers: async () => ({
      Authorization: (await Amplify.Auth.currentSession())
        .getIdToken()
        .getJwtToken(),
    }),
  },
  Auth: {
    cookieStorage: new CookieStorage({
      domain: getSharedHostname(),
      sameSite: 'lax'
    })
  }
});

async function clearOldErrorKeys() {
  //clear any error logs older than 7 days
  const allLocalDbKeys = await localDbKeys();
  const errorKeys =  allLocalDbKeys.filter((key) => key.startsWith("error"));
  errorKeys.forEach((key) => {
    try {
      if (new Date(key.substring(6,30)) < new Date(new Date().setDate(new Date().getDate()-7))) {
        localDbDelete(key);
        console.log('Expired error removed', key)
      }
    }
    catch {
      localDbDelete(key);
    }
  })
}

function setCognitoTokens({
  userIdAuth,
  cognitoId,
  refreshToken,
  idToken
}){
  Cookie.set(
    `CognitoIdentityServiceProvider.${ cognitoId }.LastAuthUser`,
    userIdAuth,
    {
      expires: 999999,
      domain: getSharedHostname()
    }
  )
  Cookie.set(
    `CognitoIdentityServiceProvider.${ cognitoId }.${ userIdAuth }.refreshToken`,
    refreshToken,
    {
      expires: 999999,
      domain: getSharedHostname()
    }
  )
  Cookie.set(
    `CognitoIdentityServiceProvider.${ cognitoId }.${ userIdAuth }.idToken`,
    idToken,
    {
      expires: 999999,
      domain: getSharedHostname()
    }
  )
}

function decodeURL() {
  const searchParams = new URLSearchParams(window.location.search);
  const data = searchParams.get('data');
  return JSON.parse(atob(data))
}

async function checkUrl() {
  const {
    idToken, refreshToken, userIdAuth
  } = decodeURL() || {}

  if(!idToken || !refreshToken || !userIdAuth) {
    throw Error('Missing user authentication data. Returning to login.')
  }

  setCognitoTokens({
    cognitoId: awsExports.aws_user_pools_web_client_id,
    userIdAuth,
    refreshToken,
    idToken
  })
}

function loadTenant() {
  try {
    const { tenantId } = decodeURL()
    const tenantIdCookie = Cookie.get('tenantId')

  if (tenantId) {
    Cookie.set('tenantId', tenantId, {
      domain: getSharedHostname(),
      expires: 999999
    })
    
    window.tenantId = tenantId
  } else if (tenantIdCookie) {
    window.tenantId = tenantIdCookie
  }

  console.info({tenantId, tenantIdCookie}, window.tenantId)
  } catch (error) {
    console.error('Decode URL failed.')
  }
}

async function getOnlineStatus() {
  // Fire a quick HEAD request to google.com to establish online connection.
  let isOnline

  try {
    const response = await fetch(
      'https://www.google.com',
      {
        method: 'HEAD',
        mode: 'no-cors'
      }
    )

    if (response) {
      // device is connected to the internet
      isOnline = true
    }      
  } catch (err) {
    // device is not connected to the internet
    isOnline = false
  }

  return isOnline
}

async function renderApp() {
  loadTenant()
  
  window.history.replaceState(null, '', window.location.pathname)
  
  const online = getOnlineStatus() || false

  if (!online) {
    try {
      await getCurrentSession()

      alert('Offline mode.')
    } catch (err) {
      alert('Application is offline, but there is no user session.')
    }
  }

  const fallbackRender = ({ error, resetErrorBoundary }) => (
    <ErrorPage 
      onRetry={() => resetErrorBoundary()}
      error={error.message}
    />
  )

  const root = ReactDOM.createRoot(document.getElementById('root'));
  
  root.render(
    <React.StrictMode>
      <ErrorBoundary fallbackRender={fallbackRender}>
        <OnlineContextProvider>
          <EntityDataProvider>
            <App />
          </EntityDataProvider>
        </OnlineContextProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );

  // If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
}

async function run() {
  clearOldErrorKeys()

  try {
    await Auth.currentSession()
    console.info('session from cookie')
    await renderApp()
  } catch (authError) {
    console.error(`User Authentication failed: ${authError}`)

    try {
      await checkUrl() 
      await renderApp()
    } catch (error) {
      console.error('URL decoding error: ', error)
      console.info('Logging user out')
      
      await logOut(true, true)
    }
  }
}

run();
